import type { ImgHTMLAttributes } from "react";

import fielmannLogoBlack from "@/assets/images/fielmann-logo-black.svg";
import fielmannLogoWhite from "@/assets/images/fielmann-logo-white.svg";

type FielmannLogoColor = "black" | "white";
type FielmannLogoSize = "header" | "footer";

const LOGO_SOURCES: Record<FielmannLogoColor, string> = {
  black: fielmannLogoBlack,
  white: fielmannLogoWhite,
};

// How Fielmann logo sizes work here:
//   The prisma design system sets Tailwind's spacing unit to 16px (Tailwind's
//   default is 4px). Every size class is therefore a multiple of 16:
//     h-1 = 16px, h-1.5 = 24px, h-1.75 = 28px, h-2 = 32px.
//   We size the logo with these classes so we stay on the design-system scale
//   instead of hard-coding pixel values.
//   - Header: h-1 (16px) on mobile, h-1.75 (28px) on wider screens.
//   - Footer: h-1 (16px), which matches the logo SVG's own height exactly.
const LOGO_SIZES: Record<
  FielmannLogoSize,
  { className: string; height: number; width: number }
> = {
  header: {
    className: "h-1 w-auto sm:h-1.75",
    height: 28,
    // Width feeds `--breakpoint-header-inline` in `index.css` — update both together.
    width: 125,
  },
  footer: {
    className: "h-1 w-auto",
    height: 16,
    width: 71,
  },
};

interface FielmannLogoProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "alt" | "height" | "src" | "width"
> {
  alt?: string;
  color?: FielmannLogoColor;
  size?: FielmannLogoSize;
}

/**
 * Fielmann wordmark rendered as an <img>.
 *
 * `color` selects the black or white SVG source — use black on light
 * backgrounds (header) and white on dark backgrounds (footer). `size` selects
 * a preset slot (header or footer) that pins the logo height to the prisma
 * spacing scale; see LOGO_SIZES below for the exact class-to-pixel mapping.
 * Callers can pass extra `className` to override or extend sizing.
 */
export function FielmannLogo({
  alt = "Fielmann",
  className,
  color = "black",
  size = "footer",
  ...imageProps
}: FielmannLogoProps) {
  const logoSize = LOGO_SIZES[size];
  const logoClassName = className
    ? `${logoSize.className} ${className}`
    : logoSize.className;

  return (
    <img
      {...imageProps}
      src={LOGO_SOURCES[color]}
      alt={alt}
      width={logoSize.width}
      height={logoSize.height}
      className={logoClassName}
    />
  );
}
